import {
  ArrowsRightLeftIcon,
  ChatBubbleOvalLeftEllipsisIcon,
  DocumentTextIcon,
  GlobeAltIcon,
  ListBulletIcon,
  PhotoIcon,
  RectangleGroupIcon,
  TableCellsIcon,
  UserCircleIcon,
  UserGroup,
} from 'components'
import { produce } from 'immer'
import { startCase, uniq } from 'lodash-es'
import { ILayout, ITemplate, LayoutType, TemplateRouting } from 'types'
import { removeEmpty } from 'utils'

/** Clean up the template by removing empty fields before saving to keep things tidy and enable dirty checking while keeping draft changes */
export function cleanTemplate(template?: ITemplate | null): ITemplate | null {
  if (!template) return null

  const cleaned = removeEmpty(template)

  return produce(cleaned, draft => {
    draft.registration?.fields?.forEach(field => {
      if (!field.label.en) field.label.en = startCase(field.property)
      if (field.required === false) delete field.required
    })
    pruneUnusedTraitsFromTraitMap(draft)
  })
}

function pruneUnusedTraitsFromTraitMap(draft: ITemplate): void {
  if (!draft.traitMap) return

  const routingTraitKeys = draft.routing ? getRoutingKeys(draft.routing).traitKeys : []
  const layoutTraitKeys = draft.layoutMap
    ? getReferencedTraitKeysFromLayoutMap(draft.layoutMap)
    : []
  const keep = new Set([...routingTraitKeys, ...layoutTraitKeys])

  for (const key of Object.keys(draft.traitMap)) {
    const numericKey = Number(key)
    if (!keep.has(numericKey)) {
      delete draft.traitMap[numericKey]
    }
  }

  if (Object.keys(draft.traitMap).length === 0) {
    delete draft.traitMap
  }
}

function getReferencedTraitKeysFromLayoutMap(layoutMap: Record<number, ILayout>): number[] {
  const out = new Set<number>()
  for (const layout of Object.values(layoutMap)) {
    collectReferencedTraitKeysFromLayout(layout, out)
  }
  return [...out]
}

function collectReferencedTraitKeysFromLayout(layout: ILayout, out: Set<number>): void {
  if (layout.type === 'trait') {
    out.add(layout.traitKey)
    return
  }

  if (layout.type === 'selected-traits') {
    out.add(layout.traitGroupKey)
  }

  if (!('layouts' in layout)) return

  for (const item of layout.layouts) {
    if (item.traitGroupKey != null) {
      out.add(item.traitGroupKey)
    }
    item.requiredTrait?.forEach(rt => {
      out.add(rt.key)
    })
  }
}

export type LayoutWithKeyPath = ILayout & { keyPath: string }

export const keyFromPath = (keyPath: string) => Number(keyPath.split('/').pop())

/** Remove layout from parent and safely delete it and any children that are not referenced elsewhere in layoutMap */
export function safelyRemoveLayout(template: ITemplate, keyPath: string) {
  if (!keyPath) return

  const key = keyFromPath(keyPath)
  const layout = template.layoutMap?.[key]
  if (!layout) return

  const updatedMap = { ...template.layoutMap }

  // Remove references to the selected layout from its parent
  const pathKeys = keyPath.split('/').map(k => parseInt(k))
  if (pathKeys.length > 1) {
    const parentKey = pathKeys[pathKeys.length - 2]
    const parentLayout = updatedMap[parentKey]

    if (parentLayout) {
      if ('layouts' in parentLayout) {
        const filteredChildren = parentLayout.layouts.filter(item => item.key !== key)
        updatedMap[parentKey] = { ...parentLayout, layouts: filteredChildren }
      }
      if (parentLayout.action?.layoutKey === key) parentLayout.action = undefined
    }
  }

  const getAllChildKeys = (layout: ILayout): number[] => {
    const childKeys: number[] = []

    if ('layouts' in layout) {
      for (const layoutItem of layout.layouts) {
        if (layoutItem.key != null) {
          childKeys.push(layoutItem.key)
          const childLayout = updatedMap[layoutItem.key]
          if (childLayout) childKeys.push(...getAllChildKeys(childLayout))
        }
      }
    }

    if (layout.action) {
      const actionLayout = updatedMap[layout.action.layoutKey]
      if (actionLayout) childKeys.push(...getAllChildKeys(actionLayout))
    }

    return childKeys
  }

  const getLayoutReferences = (targetKey: number): number => {
    let count = 0

    if (template.layoutShortcut?.key === targetKey) {
      count++
    }

    for (const [key, layout] of Object.entries(updatedMap)) {
      if (parseInt(key) === targetKey) continue

      if ('layouts' in layout && layout.layouts) {
        for (const layoutItem of layout.layouts) {
          if (layoutItem.key === targetKey) {
            count++
          }
        }
      }

      if (layout.action?.layoutKey === targetKey) {
        count++
      }
    }

    return count
  }

  const childKeys = getAllChildKeys(layout)
  const allKeysToCheck = [key, ...childKeys]

  // Delete layouts that have no references
  for (const keyToCheck of allKeysToCheck) {
    const refCount = getLayoutReferences(keyToCheck)
    if (refCount === 0) {
      delete updatedMap[keyToCheck]
    }
  }

  return updatedMap
}

export const allLayoutTypes: LayoutType[] = [
  'flex',
  'grid',
  'tabs',
  'carousel',
  'text',
  'media',
  'greeting',
  'tool',
  'trait',
  'selected-traits',
]

export const containerLayoutTypes: LayoutType[] = ['flex', 'grid', 'tabs', 'carousel']

export const iconByLayoutType: Record<LayoutType, React.ComponentType<any>> = {
  carousel: ArrowsRightLeftIcon,
  flex: RectangleGroupIcon,
  greeting: ChatBubbleOvalLeftEllipsisIcon,
  grid: TableCellsIcon,
  media: PhotoIcon,
  'selected-traits': UserGroup,
  tabs: ListBulletIcon,
  text: DocumentTextIcon,
  tool: GlobeAltIcon,
  trait: UserCircleIcon,
}

export function getRoutingKeys(routing?: TemplateRouting) {
  let layoutKeys: number[] = []
  let traitKeys: number[] = []
  if (!routing) return { layoutKeys, traitKeys }

  if (routing.type === 'layout') layoutKeys.push(routing.key)
  else traitKeys.push(routing.key)

  const defaultKeys = getRoutingKeys(routing.default)
  layoutKeys = layoutKeys.concat(defaultKeys.layoutKeys)
  traitKeys = traitKeys.concat(defaultKeys.traitKeys)
  routing.valueRoutes?.forEach(r => {
    const keys = getRoutingKeys(r.to)
    layoutKeys = layoutKeys.concat(keys.layoutKeys)
    traitKeys = traitKeys.concat(keys.traitKeys)
  })

  return { layoutKeys: uniq(layoutKeys), traitKeys: uniq(traitKeys) }
}

export function createLayout(layoutType: LayoutType): ILayout {
  switch (layoutType) {
    case 'flex':
      return { type: 'flex', direction: 'column', layouts: [] }
    case 'grid':
      return { type: 'grid', columnCount: 2, layouts: [] }
    case 'tabs':
      return { type: 'tabs', orientation: 'horizontal', layouts: [] }
    case 'carousel':
      return { type: 'carousel', layouts: [] }
    case 'text':
      return { type: 'text', text: { en: '' } }
    case 'media':
      return { type: 'media', mediaId: '' }
    case 'greeting':
      return { type: 'greeting' }
    case 'tool':
      return { type: 'tool', toolId: '' }
    case 'trait':
      return { type: 'trait', traitKey: 0, valueIndex: 0 }
    case 'selected-traits':
      return { type: 'selected-traits', traitGroupKey: 0 }
  }
}
